/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from "@tanstack/react-query";
import { useTypeIdsByBehavior } from "@/hooks/useClinicAppointmentTypes";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { parseISODateOrDateTime, toISODateOnlyLocal } from "@/lib/date-utils";
import i18n from "@/i18n";
import type {
  PatientDayEvent,
  PatientDayEventPermissions,
} from "@/components/calendar/types/patient-day-events";

interface UsePatientDayEventsInput {
  petId?: string | null;
  clinicId?: string | null;
  appointmentDate?: string | Date | null;
  permissions: PatientDayEventPermissions;
  includeGrooming?: boolean;
}

type VaccinationEventRow = {
  id: string;
  application_date: string | null;
  created_at: string | null;
  medical_record_id: string | null;
  notes: string | null;
  item_id?: { name?: string | null; product_subtype?: { name?: string | null } | null } | null;
};

const truncate = (value?: string | null, max = 140): string | undefined => {
  if (!value) return undefined;
  const normalized = value.trim();
  if (!normalized) return undefined;
  if (normalized.length <= max) return normalized;
  return `${normalized.slice(0, max).trim()}...`;
};

const normalizeDateTime = (value?: string | null): Date | null => {
  if (!value) return null;
  return parseISODateOrDateTime(value);
};

const combineDateAndTime = (dateOnly?: string | null, dateTime?: string | null): Date | null => {
  if (!dateOnly) return normalizeDateTime(dateTime);
  const date = normalizeDateTime(dateOnly);
  if (!date) return normalizeDateTime(dateTime);

  const dateWithTime = normalizeDateTime(dateTime);
  if (!dateWithTime) return date;

  const combined = new Date(date);
  combined.setHours(
    dateWithTime.getHours(),
    dateWithTime.getMinutes(),
    dateWithTime.getSeconds(),
    dateWithTime.getMilliseconds(),
  );
  return combined;
};

const toEventTime = (date: Date): { occurredAt: string; displayTime: string } => ({
  occurredAt: date.toISOString(),
  displayTime: format(date, "HH:mm"),
});

const isSameLocalDay = (value: Date, dayKey: string) => toISODateOnlyLocal(value) === dayKey;

export function usePatientDayEvents({
  petId,
  clinicId,
  appointmentDate,
  permissions,
  includeGrooming = true,
}: UsePatientDayEventsInput) {
  const supabaseAny = supabase as any;
  const groomingTypeIds = useTypeIdsByBehavior("banho_tosa");

  return useQuery({
    queryKey: [
      "patient-day-events",
      petId ?? null,
      clinicId ?? null,
      appointmentDate instanceof Date ? appointmentDate.toISOString() : appointmentDate ?? null,
      permissions,
      includeGrooming,
      groomingTypeIds,
    ],
    enabled: Boolean(petId && clinicId && appointmentDate),
    queryFn: async () => {
      if (!petId || !clinicId || !appointmentDate) return [] as PatientDayEvent[];

      const baseDate = appointmentDate instanceof Date ? appointmentDate : new Date(appointmentDate);
      if (Number.isNaN(baseDate.getTime())) return [] as PatientDayEvent[];

      const dayKey = toISODateOnlyLocal(baseDate);

      const tasks: Array<Promise<PatientDayEvent[]>> = [];

      if (permissions.consultation) {
        tasks.push(
          (async () => {
            const { data, error } = await supabase
              .from("medical_records")
              .select("id, pet_id, clinic_id, consultation_date, chief_complaint, treatment")
              .eq("pet_id", petId)
              .eq("clinic_id", clinicId)
              .is("deleted_at", null)
              .order("consultation_date", { ascending: false });

            if (error) throw error;

            return (data ?? [])
              .map((record) => {
                const date = normalizeDateTime(record.consultation_date);
                if (!date) return null;
                if (!isSameLocalDay(date, dayKey)) return null;
                const eventTime = toEventTime(date);

                return {
                  id: `consultation-${record.id}`,
                  type: "consultation",
                  petId,
                  clinicId,
                  occurredAt: eventTime.occurredAt,
                  displayTime: eventTime.displayTime,
                  title: record.chief_complaint?.trim() || i18n.t("patients:ownerHistory.typeLabels.consultation"),
                  subtitle: truncate(record.treatment),
                  medicalRecordId: record.id,
                  sourceId: record.id,
                  fallbackPayload: { type: "consultation", sourceId: record.id },
                } satisfies PatientDayEvent;
              })
              .filter((event) => event !== null);
          })(),
        );
      }

      if (permissions.vaccine || permissions.applications) {
        tasks.push(
          (async () => {
            const { data, error } = await supabaseAny
              .from("pet_applications")
              .select(`
                id,
                pet_id,
                clinic_id,
                application_date,
                created_at,
                medical_record_id,
                notes,
                item_id:items!pet_applications_item_id_fkey(name, product_subtype:product_subtypes(name))
              `)
              .eq("pet_id", petId)
              .eq("clinic_id", clinicId)
              .order("created_at", { ascending: false });

            if (error) throw error;

            return ((data ?? []) as VaccinationEventRow[])
              .map((row) => {
                const date = combineDateAndTime(row.application_date, row.created_at);
                if (!date) return null;
                if (!isSameLocalDay(date, dayKey)) return null;
                const eventTime = toEventTime(date);
                const subtype = row.item_id?.product_subtype?.name?.toLowerCase() ?? "vacina";
                const subtypeLabel = subtype === "medicamento"
                  ? i18n.t("patients:phase8.patientDayEvents.medicationLabel")
                  : i18n.t("patients:ownerHistory.typeLabels.vaccine");
                const title =
                  row?.item_id?.name ||
                  i18n.t("patients:ownerHistory.fallbacks.application");

                return {
                  id: `application-${row.id}`,
                  type: "vaccine",
                  petId,
                  clinicId,
                  occurredAt: eventTime.occurredAt,
                  displayTime: eventTime.displayTime,
                  title,
                  subtitle: truncate([subtypeLabel, row.notes].filter(Boolean).join(" • ")),
                  medicalRecordId: row.medical_record_id,
                  sourceId: row.id,
                  fallbackPayload: { type: "vaccine", sourceId: row.id },
                } satisfies PatientDayEvent;
              })
              .filter((event) => event !== null);
          })(),
        );
      }

      if (permissions.exam) {
        tasks.push(
          (async () => {
            const { data, error } = await supabase
              .from("pet_attachments")
              .select("id, pet_id, clinic_id, exam_date, created_at, medical_record_id, type, category, description, file_name, filename, original_name")
              .eq("pet_id", petId)
              .eq("clinic_id", clinicId)
              .is("deleted_at", null)
              .order("created_at", { ascending: false });

            if (error) throw error;

            return (data ?? [])
              .map((row) => {
                const date = combineDateAndTime(row.exam_date, row.created_at);
                if (!date) return null;
                if (!isSameLocalDay(date, dayKey)) return null;
                const eventTime = toEventTime(date);
                const title =
                  row.type?.trim() ||
                  row.category?.trim() ||
                  row.original_name?.trim() ||
                  row.file_name?.trim() ||
                  row.filename?.trim() ||
                  i18n.t("patients:ownerHistory.typeLabels.exam");

                return {
                  id: `exam-${row.id}`,
                  type: "exam",
                  petId,
                  clinicId,
                  occurredAt: eventTime.occurredAt,
                  displayTime: eventTime.displayTime,
                  title,
                  subtitle: truncate(row.description || row.category),
                  medicalRecordId: row.medical_record_id,
                  sourceId: row.id,
                  fallbackPayload: { type: "exam", sourceId: row.id },
                } satisfies PatientDayEvent;
              })
              .filter((event) => event !== null);
          })(),
        );
      }

      if (permissions.prescription) {
        tasks.push(
          (async () => {
            const { data, error } = await supabase
              .from("medical_prescriptions")
              .select("id, pet_id, clinic_id, issued_at, created_at, medical_record_id, observations, type")
              .eq("pet_id", petId)
              .eq("clinic_id", clinicId)
              .order("created_at", { ascending: false });

            if (error) throw error;

            return (data ?? [])
              .map((row) => {
                const date = normalizeDateTime(row.issued_at ?? row.created_at);
                if (!date) return null;
                if (!isSameLocalDay(date, dayKey)) return null;
                const eventTime = toEventTime(date);
                const subtitle = row.type
                  ? truncate(
                    `${i18n.t("patients:phase8.patientDayEvents.prescriptionTypePrefix")}: ${row.type}${row.observations ? ` • ${row.observations}` : ""}`,
                  )
                  : truncate(row.observations);

                return {
                  id: `prescription-${row.id}`,
                  type: "prescription",
                  petId,
                  clinicId,
                  occurredAt: eventTime.occurredAt,
                  displayTime: eventTime.displayTime,
                  title: i18n.t("patients:ownerHistory.typeLabels.prescription"),
                  subtitle,
                  medicalRecordId: row.medical_record_id,
                  sourceId: row.id,
                  fallbackPayload: { type: "prescription", sourceId: row.id },
                } satisfies PatientDayEvent;
              })
              .filter((event) => event !== null);
          })(),
        );
      }

      if (includeGrooming && permissions.grooming) {
        tasks.push(
          (async () => {
            if (groomingTypeIds.length === 0) return [];
            const { data, error } = await supabase
              .from("appointments")
              .select("id, pet_id, clinic_id, starts_at, created_at, notes, status")
              .eq("pet_id", petId)
              .eq("clinic_id", clinicId)
              .in("type", groomingTypeIds)
              .is("deleted_at", null)
              .order("starts_at", { ascending: false });

            if (error) throw error;

            return (data ?? [])
              .map((row) => {
                const date = normalizeDateTime(row.starts_at ?? row.created_at);
                if (!date) return null;
                if (!isSameLocalDay(date, dayKey)) return null;
                const eventTime = toEventTime(date);
                const statusLabel = row.status;

                return {
                  id: `grooming-${row.id}`,
                  type: "grooming",
                  petId,
                  clinicId,
                  occurredAt: eventTime.occurredAt,
                  displayTime: eventTime.displayTime,
                  title: i18n.t("patients:ownerHistory.typeLabels.grooming"),
                  subtitle: truncate([statusLabel, row.notes].filter(Boolean).join(" • ")),
                  medicalRecordId: null,
                  sourceId: row.id,
                  fallbackPayload: { type: "grooming", sourceId: row.id },
                } satisfies PatientDayEvent;
              })
              .filter((event) => event !== null);
          })(),
        );
      }

      const grouped = await Promise.all(tasks);
      return grouped
        .flat()
        .sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime());
    },
  });
}
